import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Loader } from '@Sage/hoi-poi-ui';

import { TransitionGroup, FadeTransition } from 'components/Transitions';
import { VIEWS } from '../../constants';
import CenteredActionScreen from '../CenteredActionScreen';
import CompanySelection from '../CompanySelection';
import SyncInProgress from '../SyncInProgress';
import Warning from 'components/SvgIcons/Warning';
import { companies as companiesLottie, signIn as signInLottie } from '../../lottieFiles';
import { contactUsAction, primaryAction } from './actionHelpers';

const OnboardingViewContent = ({
    blockerLiterals,
    classes,
    companies,
    isSubmitting,
    isSyncRunning,
    logo,
    maxSelectable,
    onConnectSageId,
    onOpenConfirm,
    onRetryBootstrap,
    onRetryCompanies,
    onRetrySync,
    onSyncProgressComplete,
    onToggleCompany,
    syncActiveStep,
    syncErrorCode,
    syncIsSuccess,
    onContactUs,
    selectedIds,
    t,
    view,
}) => {
    const getViewContent = () => {
        switch (view) {
            case VIEWS.BOOTSTRAP:
                return (
                    <div className={classes.modalLoader}>
                        {logo}
                        <Loader size="big" />
                    </div>
                );
            case VIEWS.SAGEID_LOGIN:
                return (
                    <CenteredActionScreen
                        title={t('label_s200uki_sageid_login_title')}
                        body={t('label_s200uki_sageid_login_body')}
                        actions={[
                            primaryAction({
                                label: t('label_s200uki_sageid_login_cta'),
                                onClick: onConnectSageId,
                            }),
                        ]}
                        illustration={signInLottie}
                    />
                );
            case VIEWS.SAGEID_SUCCESS:
                return (
                    <CenteredActionScreen
                        title={t('label_s200uki_sageid_auth_success_title')}
                        body={t('label_s200uki_sageid_auth_success_body')}
                        illustration={signInLottie}
                    />
                );
            case VIEWS.ADMIN_CREATE_FAILED:
                return (
                    <CenteredActionScreen
                        title={t('label_s200uki_admin_create_failed_title')}
                        body={t('label_s200uki_admin_create_failed_body')}
                        actions={[
                            primaryAction({
                                label: t('label_s200uki_try_again'),
                                onClick: onRetryBootstrap,
                                isLoading: isSubmitting,
                            }),
                        ]}
                    />
                );
            case VIEWS.COMPANIES:
                return (
                    <CompanySelection
                        t={t}
                        companies={companies}
                        selectedIds={selectedIds}
                        maxSelectable={maxSelectable}
                        onToggleCompany={onToggleCompany}
                        onConfirmClick={onOpenConfirm}
                        isLoading={isSubmitting}
                    />
                );
            case VIEWS.COMPANIES_ERROR:
                return (
                    <CenteredActionScreen
                        title={t('label_s200uki_companies_load_error_title')}
                        body={t('label_s200uki_companies_load_error_body')}
                        actions={[
                            contactUsAction({ t, onContactUs, type: 'secondary' }),
                            primaryAction({
                                label: t('label_s200uki_refresh'),
                                icon: 'refresh',
                                onClick: onRetryCompanies,
                                isLoading: isSubmitting,
                            }),
                        ]}
                        illustration={companiesLottie}
                    />
                );
            case VIEWS.NO_COMPANIES:
                return (
                    <CenteredActionScreen
                        title={t('label_s200uki_no_companies_title')}
                        body={t('label_s200uki_no_companies_body')}
                        actions={[contactUsAction({ t, onContactUs })]}
                        illustration={companiesLottie}
                    />
                );
            case VIEWS.SYNC_PROGRESS:
                return (
                    <SyncInProgress
                        activeStep={syncActiveStep}
                        isSuccess={syncIsSuccess}
                        onComplete={onSyncProgressComplete}
                        t={t}
                    />
                );
            case VIEWS.SYNC_FAILED:
                return (
                    <CenteredActionScreen
                        title={
                            syncErrorCode === 'CREATE_TIMEOUT'
                                ? t('label_s200uki_sync_failed_timeout_title')
                                : t('label_s200uki_sync_failed_title')
                        }
                        body={
                            syncErrorCode === 'CREATE_TIMEOUT'
                                ? t('label_s200uki_sync_failed_timeout_body')
                                : t('label_s200uki_sync_failed_body')
                        }
                        actions={[
                            primaryAction({
                                label: t('label_s200uki_try_again'),
                                icon: 'refresh',
                                onClick: onRetrySync,
                                isLoading: isSyncRunning,
                            }),
                        ]}
                        illustration={companiesLottie}
                    />
                );
            case VIEWS.BLOCKER:
                return (
                    <CenteredActionScreen
                        title={blockerLiterals?.title}
                        body={blockerLiterals?.body}
                        actions={[contactUsAction({ t, onContactUs })]}
                        illustration={companiesLottie}
                    />
                );
            case VIEWS.MOBILE_BLOCKER:
                return (
                    <CenteredActionScreen
                        title={t('label_s200uki_mobile_blocker_title')}
                        body={t('label_s200uki_mobile_blocker_body')}
                        illustrationNode={<Warning />}
                        useIllustrationBackdrop={false}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <TransitionGroup>
            <FadeTransition transitionKey={view} show>
                {getViewContent()}
            </FadeTransition>
        </TransitionGroup>
    );
};

OnboardingViewContent.propTypes = {
    blockerLiterals: PropTypes.shape({
        body: PropTypes.string,
        title: PropTypes.string,
    }),
    classes: PropTypes.object.isRequired,
    companies: PropTypes.array.isRequired,
    isSubmitting: PropTypes.bool,
    isSyncRunning: PropTypes.bool,
    logo: PropTypes.node,
    maxSelectable: PropTypes.number.isRequired,
    onContactUs: PropTypes.func.isRequired,
    onConnectSageId: PropTypes.func,
    onOpenConfirm: PropTypes.func,
    onRetryBootstrap: PropTypes.func,
    onRetryCompanies: PropTypes.func,
    onRetrySync: PropTypes.func,
    onSyncProgressComplete: PropTypes.func,
    onToggleCompany: PropTypes.func,
    syncActiveStep: PropTypes.oneOf([2, 3, 4]),
    syncErrorCode: PropTypes.string,
    syncIsSuccess: PropTypes.bool,
    selectedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    t: PropTypes.func.isRequired,
    view: PropTypes.string.isRequired,
};

OnboardingViewContent.displayName = 'OnboardingViewContent';

export default memo(OnboardingViewContent);
